import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import '../../styles/components/FormPage.css';
import { Input as FormInput, Textarea as FormTextarea } from '../FormField';
import ConfirmDialog from '../ui/ConfirmDialog';
import SearchableSelect from '../ui/SearchableSelect';
import Button from '../ui/Button';
import { useNotifications } from '../NotificationProvider';
import { parseIdFromPath } from '../../utils/jiraId';
import { errorMessageOf } from '../../utils/apiErrors';
import { reportSaveError, useFieldErrors } from './_shared';
import {
  archiveTeamMember, createTeamMember, deleteTeamMemberForever, getTeamMemberById, refreshTeamFromApi,
  restoreTeamMember, trashTeamMember, updateTeamMember,
} from '../../services/TeamService';
import { RELATIONSHIP_OPTIONS } from '../../utils/teamView';
import type { TeamMember, TeamRelationship } from '../../types/Team';

/** The task domains a person can be scoped to for assignment (fixed by the API). */
export const TEAM_DOMAIN_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'planning', label: 'Planning' },
  { value: 'relations', label: 'Relations' },
  { value: 'vendors', label: 'Vendors' },
  { value: 'finance', label: 'Finance' },
  { value: 'style', label: 'Style' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'daymanagement', label: 'Day management' },
];

type Stage = 'active' | 'archived' | 'trash';
const stageOf = (m: TeamMember | null): Stage => (m?.trashed_at ? 'trash' : m?.archived_at ? 'archived' : 'active');

interface TeamMemberFormPageProps {
  onSave: () => void;
  onCancel: () => void;
}

const asText = (value: TeamMember['responsibilities']): string =>
  Array.isArray(value) ? value.map(String).join('\n') : value ?? '';

/**
 * Add or edit a team member. The header's Save and Cancel reach this page
 * through window events (team-member-form-save / -cancel), like the other form
 * pages. The staged lifecycle (archive, trash, restore, delete forever) lives
 * in the side panel; the API refuses a permanent delete while tasks, assisted
 * guests or a login still point at the person, and says which.
 */
export const TeamMemberFormPage: React.FC<TeamMemberFormPageProps> = ({ onSave, onCancel }) => {
  const { showSuccess, showError } = useNotifications();
  const fieldErrors = useFieldErrors();
  const location = useLocation();
  const editId = useMemo(() => parseIdFromPath('TM', location.pathname, '/team-member/'), [location.pathname]);
  const prefill = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return { email: q.get('email') ?? '', name: q.get('name') ?? '' };
  }, [location.search]);

  const [member, setMember] = useState<TeamMember | null>(null);
  const [loaded, setLoaded] = useState(!editId);
  const [name, setName] = useState(prefill.name);
  const [email, setEmail] = useState(prefill.email);
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [role, setRole] = useState('');
  const [relationship, setRelationship] = useState<TeamRelationship>('neutral');
  const [teams, setTeams] = useState('');
  const [domainIds, setDomainIds] = useState<string[]>([]);
  const [responsibilities, setResponsibilities] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<null | 'archive' | 'trash' | 'forever'>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const fill = (m: TeamMember) => {
    setMember(m);
    setName(m.name ?? '');
    setEmail(m.email ?? '');
    setPhone(m.phone ?? '');
    setWhatsapp(m.whatsapp ?? '');
    setRole(m.role ?? '');
    setRelationship(m.relationship ?? 'neutral');
    setTeams((m.teams ?? []).join(', '));
    setDomainIds(m.domain_ids ?? []);
    setResponsibilities(asText(m.responsibilities));
    setNotes(m.notes ?? '');
    setIsActive(m.is_active !== false);
  };

  useEffect(() => {
    if (!editId) return;
    let alive = true;
    setLoaded(false);
    setLoadFailed(false);
    getTeamMemberById(editId)
      .then(found => { if (alive) { if (found) fill(found); else setLoadFailed(true); setLoaded(true); } })
      .catch(() => { if (alive) { setLoadFailed(true); setLoaded(true); } });
    return () => { alive = false; };
  }, [editId]);

  const stage = stageOf(member);
  const readOnly = editId !== null && stage !== 'active';

  const handleSave = useCallback(async () => {
    if (saving || readOnly) return;
    if (!name.trim()) { showError('Name is required.'); return; }
    fieldErrors.clear();
    setSaving(true);
    try {
      const input = {
        name: name.trim(), email, phone, whatsapp, role, relationship, notes, is_active: isActive,
        teams: teams.split(',').map(t => t.trim()).filter(Boolean),
        domain_ids: domainIds,
        responsibilities,
      };
      if (editId) await updateTeamMember(editId, input);
      else await createTeamMember(input);
      await refreshTeamFromApi().catch(() => []);
      showSuccess(editId ? 'Team member updated' : 'Team member added', { persistent: true });
      onSave();
    } catch (err) {
      reportSaveError(err, fieldErrors, showError, 'Failed to save team member');
    } finally {
      setSaving(false);
    }
  }, [saving, readOnly, name, email, phone, whatsapp, role, relationship, notes, isActive, teams, domainIds, responsibilities, editId, fieldErrors, showError, showSuccess, onSave]);

  useEffect(() => {
    const save = () => { void handleSave(); };
    window.addEventListener('team-member-form-save', save);
    window.addEventListener('team-member-form-cancel', onCancel);
    return () => {
      window.removeEventListener('team-member-form-save', save);
      window.removeEventListener('team-member-form-cancel', onCancel);
    };
  }, [handleSave, onCancel]);

  const move = async (action: () => Promise<unknown>, done: string) => {
    try {
      await action();
      await refreshTeamFromApi().catch(() => []);
      showSuccess(done, { persistent: true });
      setConfirm(null);
      onCancel();
    } catch (err) {
      const message = errorMessageOf(err, 'That did not go through. Please try again.');
      // A dialog shows its own error; a button with no dialog (Restore) must still say it failed.
      if (confirm !== null) setConfirmError(message); else showError(message);
    }
  };

  const toggleDomain = (value: string) =>
    setDomainIds(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));

  if (!loaded) return <div className="detail-layout"><p role="status">Loading…</p></div>;
  if (loadFailed) return <div className="detail-layout"><p role="alert">We could not find this team member. They may have been deleted.</p></div>;

  return (
    <div className="detail-layout">
      <div className="detail-main form-card">
        {readOnly && (
          <p className="form-hint" role="status">
            {stage === 'trash' ? 'This person is in the trash.' : 'This person is archived.'} Restore them to edit.
          </p>
        )}
        <div className="form-col-2">
          <FormInput
            label="Name *"
            value={name}
            disabled={readOnly}
            error={fieldErrors.errorFor('name')}
            onChange={e => { setName(e.target.value); fieldErrors.clear('name'); }}
          />
          <FormInput label="Role or title" placeholder="e.g. Day coordinator" value={role} disabled={readOnly} onChange={e => setRole(e.target.value)} />
        </div>
        <div className="form-col-2">
          <FormInput
            label="Email"
            type="email"
            value={email}
            disabled={readOnly}
            error={fieldErrors.errorFor('email')}
            onChange={e => { setEmail(e.target.value); fieldErrors.clear('email'); }}
          />
          <FormInput label="Phone" type="tel" value={phone} disabled={readOnly} error={fieldErrors.errorFor('phone')} onChange={e => { setPhone(e.target.value); fieldErrors.clear('phone'); }} />
        </div>
        <div className="form-col-2">
          <FormInput label="WhatsApp" type="tel" value={whatsapp} disabled={readOnly} onChange={e => setWhatsapp(e.target.value)} />
          <div className="form-group">
            <label className="form-label">Side</label>
            <SearchableSelect value={relationship} onChange={v => setRelationship(v as TeamRelationship)} options={RELATIONSHIP_OPTIONS} />
          </div>
        </div>
        <FormInput label="Teams (comma separated)" placeholder="e.g. Check-in, Security" value={teams} disabled={readOnly} onChange={e => setTeams(e.target.value)} />
        <fieldset className="form-group" disabled={readOnly}>
          <legend className="form-label">Task areas this person can be assigned in</legend>
          {TEAM_DOMAIN_OPTIONS.map(opt => (
            <label key={opt.value} className="form-check">
              <input type="checkbox" checked={domainIds.includes(opt.value)} onChange={() => toggleDomain(opt.value)} /> {opt.label}
            </label>
          ))}
        </fieldset>
        <FormTextarea label="Responsibilities (one per line)" rows={4} value={responsibilities} disabled={readOnly} onChange={e => setResponsibilities(e.target.value)} />
        <FormTextarea label="Notes" rows={3} value={notes} disabled={readOnly} onChange={e => setNotes(e.target.value)} />
        <label className="form-check">
          <input type="checkbox" checked={isActive} disabled={readOnly} onChange={e => setIsActive(e.target.checked)} /> Active (untick to deactivate without archiving)
        </label>
      </div>

      <aside className="detail-sidebar">
        <section className="side-section">
          <h4>About login access</h4>
          <p className="form-hint form-hint--flush">
            Adding an e-mail also creates a sign-in for that person with view-only access, unless one already exists. Only an owner can give anyone a higher role, in Settings.
          </p>
        </section>
        {editId && member && (
          <section className="side-section">
            <h4>Lifecycle</h4>
            <div className="team-form-actions">
              {stage === 'active' && <Button variant="secondary" size="sm" onClick={() => { setConfirmError(null); setConfirm('archive'); }}>Archive</Button>}
              {stage !== 'trash' && <Button variant="secondary" size="sm" onClick={() => { setConfirmError(null); setConfirm('trash'); }}>Move to trash</Button>}
              {stage !== 'active' && <Button variant="secondary" size="sm" onClick={() => void move(() => restoreTeamMember(editId), `${member.name} restored`)}>Restore</Button>}
              {stage === 'trash' && <Button variant="danger" size="sm" onClick={() => { setConfirmError(null); setConfirm('forever'); }}>Delete forever</Button>}
            </div>
          </section>
        )}
      </aside>

      <ConfirmDialog
        open={confirm === 'archive'}
        onClose={() => setConfirm(null)}
        onConfirm={() => void move(() => archiveTeamMember(editId!), `${name} archived`)}
        title="Archive this person?"
        description="They are hidden from the active list but kept. You can restore them from the Archived list."
        confirmLabel="Archive"
        error={confirmError}
      />
      <ConfirmDialog
        open={confirm === 'trash'}
        onClose={() => setConfirm(null)}
        onConfirm={() => void move(() => trashTeamMember(editId!), `${name} moved to the trash`)}
        title="Move this person to the trash?"
        description="They can be restored from the Trash list until deleted forever."
        confirmLabel="Move to trash"
        error={confirmError}
      />
      <ConfirmDialog
        open={confirm === 'forever'}
        onClose={() => setConfirm(null)}
        onConfirm={() => void move(() => deleteTeamMemberForever(editId!), `${name} deleted`)}
        title="Delete this person forever?"
        subject={name}
        description="This cannot be undone. It is refused while tasks, assisted guests or a login still point at them."
        confirmLabel="Delete forever"
        variant="danger"
        error={confirmError}
      />
    </div>
  );
};

export default TeamMemberFormPage;
